import React, { useState, useEffect, useRef } from "react";
import Header from "../../../Componets/Header";
import Loadercomponent from "../../../Componets/Loader";
import toast from "react-hot-toast";
import ScrollWrapper from "../../../Componets/ScrollWrapper";
import { Button, Tag, Badge } from "antd";
import {
  EyeOutlined,
  FilePdfOutlined,
  ShoppingOutlined,
  UndoOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  InfoCircleOutlined,
  UpOutlined,
  DownOutlined,
} from "@ant-design/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import formatDateDDMMYYYY from "../../../Main/formatDate";
import { get_supplier_purchase_report } from "../../../Controllers/Dashboard/Dashboard";
import ReactToPrint from "react-to-print";

const SupplierPurchaseReport = () => {
  const [loader, setLoader] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [transactionType, setTransactionType] = useState("all");
  const componentRef = useRef();
  const printableRef = useRef();

  const headers = {
    name: "Supplier Product Transactions Report",
    components: [
      {
        type: "search",
        name: "search",
        placeholder: "Search product or supplier...",
        value: "",
      },
      {
        type: "filter",
        name: "supplier",
        placeholder: "Supplier",
        allowClear: true,
        showSearch: true,
        filterOption: false,
        api: "get-all-suppliers",
        value: null,
        options: null,
      },
      {
        type: "range",
        name: "date",
        placeholder: "Transaction Date Range",
        value: {
          start: "",
          end: "",
        },
      },
      {
        type: "filter",
        name: "branch",
        placeholder: "Branch",
        allowClear: true,
        showSearch: true,
        filterOption: false,
        api: "get-all-branches",
        value: null,
        options: null,
      },
      {
        type: "select",
        name: "transactionType",
        placeholder: "Transaction Type",
        value: "all",
        allowClear: true,
        showSearch: false,
        filterOption: true,
        options: [
          { value: "all", label: "All Transactions" },
          { value: "purchase", label: "Purchases Only" },
          { value: "return", label: "Returns Only" },
        ],
      },
      {
        type: "select",
        name: "paymentStatus",
        placeholder: "Payment Status",
        value: "all",
        allowClear: true,
        showSearch: false,
        filterOption: true,
        options: [
          { value: "all", label: "All Payment Status" },
          { value: "paid", label: "Paid" },
          { value: "partial", label: "Partial" },
          { value: "unpaid", label: "Unpaid" },
        ],
      },
    ],
    navigate: {
      tabs: [
        {
          name: "Reports",
          path: "/app/reports",
        },
        {
          name: "Supplier Product Transactions",
          path: "",
        },
      ],
    },
  };

  const [header, setHeader] = useState(headers);

  const fetchReport = async (filters = {}, page = 1) => {
    try {
      setLoader(true);

      const params = {
        search: filters.search || "",
        supplier: filters.supplier || null,
        date: filters.date || {},
        branch: filters.branch || null,
        transactionType: filters.transactionType || transactionType,
      };

      console.log("Fetching with params:", params);

      const response = await get_supplier_purchase_report(params);

      if (response?.status) {
        console.log("API Response data:", response.data.data);
        setReportData(response.data.data || []);
        setSummary(response.data.summary || {});
      } else {
        toast.error(response?.message || "Failed to fetch report");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoader(false);
    }
  };

  const formdata = (data) => {
    console.log("Form data:", data);

    let processedData = { ...data };
    if (data.supplier && typeof data.supplier === "object") {
      processedData.supplier = data.supplier._id || data.supplier.id;
    }

    // Update local state if transactionType changed
    if (
      data.transactionType !== undefined &&
      data.transactionType !== transactionType
    ) {
      setTransactionType(data.transactionType);
    }

    fetchReport(processedData, 1);
  };

  const handlePageChange = (page) => {
    const filters = {};
    headers.components.forEach((comp) => {
      if (
        comp.name !== "transactionType" &&
        header.components.find((c) => c.name === comp.name)?.value
      ) {
        filters[comp.name] = header.components.find(
          (c) => c.name === comp.name,
        )?.value;
      }
    });
    filters.transactionType = transactionType;

    fetchReport(filters, page);
  };

  const toggleRowExpand = (item) => {
    const key = `${item.supplier.id}-${item.product.id}`;
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getNetPositionTag = (item) => {
    if (item.netPosition?.isNetPositive && transactionType !== "return") {
      return (
        <Tag color="green" className="ml-2">
          Net Purchase: {item.netPosition.netQuantity}
        </Tag>
      );
    } else if (
      (item.netPosition?.netQuantity < 0 || transactionType === "return") &&
      transactionType !== "purchase"
    ) {
      return (
        <Tag color="red" className="ml-2">
          Net Return: {Math.abs(item.netPosition?.netQuantity || 0)}
        </Tag>
      );
    }
    return null;
  };

  // Function to get payment tag color for individual transactions
  const getPaymentTagColor = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "green";
      case "partial":
        return "orange";
      case "unpaid":
        return "red";
      default:
        return "default";
    }
  };

  const downloadCSV = () => {
    if (!reportData.length) {
      toast.error("No data to export");
      return;
    }

    // Collect and filter transactions
    const allTransactions = reportData.flatMap((item) =>
      (item.batchDetails || [])
        .filter(
          (batch) =>
            transactionType === "all" ||
            (transactionType === "purchase" &&
              batch.transactionType === "purchase") ||
            (transactionType === "return" &&
              batch.transactionType === "return"),
        )
        .map((batch) => {
          const isReturn = batch.transactionType === "return";
          const sign = isReturn ? -1 : 1;

          return {
            supplier: item.supplier.name,
            poNumber: batch.purchaseNumber,
            date: formatDateDDMMYYYY(batch.date),
            product: item.product.name,
            unit: item.product.unit || "N/A",
            batch: batch.batch || "N/A",
            quantity: sign * Math.abs(batch.totalQty || 0),
            price: Math.abs(batch.unitPrice || 0),
            total: sign * Math.abs(batch.lineTotal || 0),
            transactionType: batch.transactionType,
            paymentStatus:
              batch.paymentStatus || (isReturn ? "N/A" : "unknown"),
          };
        }),
    );

    if (allTransactions.length === 0) {
      toast.error("No transaction data to export");
      return;
    }

    // Separate purchase and return transactions
    const purchaseTransactions = allTransactions.filter(
      (t) => t.transactionType === "purchase",
    );
    const returnTransactions = allTransactions.filter(
      (t) => t.transactionType === "return",
    );

    // Calculate totals
    const totalPurchaseQty = purchaseTransactions.reduce(
      (sum, t) => sum + t.quantity,
      0,
    );
    const totalReturnQty = returnTransactions.reduce(
      (sum, t) => sum + t.quantity,
      0,
    );
    const netQty = totalPurchaseQty + totalReturnQty;

    const totalPurchaseAmount = purchaseTransactions.reduce(
      (sum, t) => sum + t.total,
      0,
    );
    const totalReturnAmount = returnTransactions.reduce(
      (sum, t) => sum + t.total,
      0,
    );
    const netAmount = totalPurchaseAmount + totalReturnAmount;

    // Headers
    const headers = [
      "S.no",
      "Supplier",
      "Transaction Type",
      "PO",
      "Date",
      "Product",
      "Unit",
      "Batch",
      "Qty",
      "Price (OMR)",
      "Total (OMR)",
    ];

    if (transactionType !== "return") headers.push("Payment Status");

    // Transaction rows
    const transactionRows = allTransactions.map((t, i) => [
      i + 1,
      `"${t.supplier}"`,
      t.transactionType === "purchase" ? "Purchase" : "Return",
      `"${t.poNumber}"`,
      t.date,
      `"${t.product}"`,
      t.unit,
      `"${t.batch}"`,
      t.quantity,
      t.price.toFixed(3),
      t.transactionType === "return"
        ? `-${Math.abs(t.total).toFixed(3)}`
        : t.total.toFixed(3),
      ...(transactionType !== "return"
        ? [t.transactionType === "purchase" ? t.paymentStatus : "N/A"]
        : []),
    ]);

    // Add empty line before summary
    transactionRows.push(Array(headers.length).fill(""));

    // Summary rows
    const summaryRows = [];

    if (purchaseTransactions.length > 0) {
      summaryRows.push([
        "",
        "PURCHASES TOTAL",
        "",
        "",
        "",
        "",
        "",
        "",
        totalPurchaseQty,
        "",
        totalPurchaseAmount.toFixed(3),
        ...(transactionType !== "return" ? [""] : []),
      ]);
    }

    if (returnTransactions.length > 0) {
      summaryRows.push([
        "",
        "RETURNS TOTAL",
        "",
        "",
        "",
        "",
        "",
        "",
        totalReturnQty,
        "",
        totalReturnAmount.toFixed(3),
        ...(transactionType !== "return" ? [""] : []),
      ]);
    }

    summaryRows.push([
      "",
      "NET TOTAL",
      "",
      "",
      "",
      "",
      "",
      "",
      netQty,
      "",
      netAmount.toFixed(3),
      ...(transactionType !== "return" ? [""] : []),
    ]);

    // Combine everything
    const rows = [...transactionRows, ...summaryRows];
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    // Download
    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    const filename = `Supplier_${transactionType === "purchase"
      ? "Purchases"
      : transactionType === "return"
        ? "Returns"
        : "Transactions"
      }_${new Date().toISOString().split("T")[0]}.csv`;

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderPrintableTable = () => {
    const purchases = [];
    const returns = [];

    reportData.forEach((item) => {
      (item.batchDetails || []).forEach((batch) => {
        const row = {
          supplier: item.supplier.name,
          product: item.product.name,
          date: formatDateDDMMYYYY(batch.date),
          poNumber: batch.purchaseNumber,
          batch: batch.batch || "N/A",
          qty: Math.abs(batch.totalQty || 0),
          price: batch.unitPrice || 0,
          total: Math.abs(batch.lineTotal || 0),
        };

        if (batch.transactionType === "purchase") purchases.push(row);
        else returns.push(row);
      });
    });

    // Calculate totals for summary
    const totalPurchaseQty = purchases.reduce((sum, r) => sum + r.qty, 0);
    const totalPurchaseValue = purchases.reduce((sum, r) => sum + r.total, 0);
    const totalReturnQty = returns.reduce((sum, r) => sum + r.qty, 0);
    const totalReturnValue = returns.reduce((sum, r) => sum + r.total, 0);

    return (
      <div>
        {/* Purchases Table */}
        <h3 className="font-bold mb-2">Purchases</h3>
        <table className="w-full border-collapse border border-black text-xs mb-4">
          <thead>
            <tr className="bg-gray-200">
              <th className="border p-1">S.No</th>
              <th className="border p-1">Supplier</th>
              <th className="border p-1">Product</th>
              <th className="border p-1">Date</th>
              <th className="border p-1">PO#</th>
              <th className="border p-1">Batch</th>
              <th className="border p-1 text-right">Qty</th>
              <th className="border p-1 text-right">Price</th>
              <th className="border p-1 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {purchases.map((t, i) => (
              <tr key={i}>
                <td className="border p-1">{i + 1}</td>
                <td className="border p-1">{t.supplier}</td>
                <td className="border p-1">{t.product}</td>
                <td className="border p-1">{t.date}</td>
                <td className="border p-1">#{t.poNumber}</td>
                <td className="border p-1">{t.batch}</td>
                <td className="border p-1 text-right">{t.qty}</td>
                <td className="border p-1 text-right">OMR {t.price.toFixed(3)}</td>
                <td className="border p-1 text-right">OMR {t.total.toFixed(3)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Returns Table */}
        <h3 className="font-bold mb-2">Returns</h3>
        <table className="w-full border-collapse border border-black text-xs mb-4">
          <thead>
            <tr className="bg-gray-200">
              <th className="border p-1">S.No</th>
              <th className="border p-1">Supplier</th>
              <th className="border p-1">Product</th>
              <th className="border p-1">Date</th>
              <th className="border p-1">PO#</th>
              <th className="border p-1">Batch</th>
              <th className="border p-1 text-right">Qty</th>
              <th className="border p-1 text-right">Price</th>
              <th className="border p-1 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {returns.map((t, i) => (
              <tr key={i}>
                <td className="border p-1">{i + 1}</td>
                <td className="border p-1">{t.supplier}</td>
                <td className="border p-1">{t.product}</td>
                <td className="border p-1">{t.date}</td>
                <td className="border p-1">#{t.poNumber}</td>
                <td className="border p-1">{t.batch}</td>
                <td className="border p-1 text-right">{t.qty}</td>
                <td className="border p-1 text-right">OMR {t.price.toFixed(3)}</td>
                <td className="border p-1 text-right">OMR {t.total.toFixed(3)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Summary */}
        <h3 className="font-bold mb-2">Summary</h3>
        <table className="w-full border-collapse border border-black text-xs">
          <tbody>
            <tr>
              <td className="border p-1 font-semibold">Total Purchases</td>
              <td className="border p-1 text-right">{totalPurchaseQty}</td>
              <td className="border p-1 text-right">OMR {totalPurchaseValue.toFixed(3)}</td>
            </tr>
            <tr>
              <td className="border p-1 font-semibold">Total Returns</td>
              <td className="border p-1 text-right">{totalReturnQty}</td>
              <td className="border p-1 text-right">OMR {totalReturnValue.toFixed(3)}</td>
            </tr>
            <tr>
              <td className="border p-1 font-semibold">Net Total</td>
              <td className="border p-1 text-right">{totalPurchaseQty - totalReturnQty}</td>
              <td className="border p-1 text-right">OMR {(totalPurchaseValue - totalReturnValue).toFixed(3)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };


  const renderTransactionDetails = (item) => {
    if (
      !item.batchDetails ||
      !expandedRows[`${item.supplier.id}-${item.product.id}`]
    ) {
      return null;
    }

    // Filter batch details based on transaction type
    let filteredBatchDetails = item.batchDetails;
    if (transactionType === "return") {
      filteredBatchDetails = item.batchDetails.filter(
        (b) => b.transactionType === "return",
      );
    } else if (transactionType === "purchase") {
      filteredBatchDetails = item.batchDetails.filter(
        (b) => b.transactionType === "purchase",
      );
    }

    if (filteredBatchDetails.length === 0) {
      return null;
    }

    const purchases = filteredBatchDetails.filter(
      (b) => b.transactionType === "purchase",
    );
    const returns = filteredBatchDetails.filter(
      (b) => b.transactionType === "return",
    );

    const totalPurchaseQty = purchases.reduce(
      (sum, p) => sum + (p.totalQty || 0),
      0,
    );
    const totalReturnQty = Math.abs(
      returns.reduce((sum, r) => sum + (r.totalQty || 0), 0),
    );
    const totalPurchaseValue = purchases.reduce(
      (sum, p) => sum + (p.lineTotal || 0),
      0,
    );
    const totalReturnValue = Math.abs(
      returns.reduce((sum, r) => sum + (r.lineTotal || 0), 0),
    );

    return (
      <tr className="bg-gray-50">
        <td colSpan="10" className="border p-0">
          <div className="p-4">
            {/* Transaction Details Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm bg-white border rounded-lg shadow-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 border">Type</th>
                    <th className="p-2 border">Batch/Barcode</th>
                    <th className="p-2 border">Date</th>
                    <th className="p-2 border">PO#</th>
                    <th className="p-2 border">Ordered</th>
                    <th className="p-2 border">FOC</th>
                    <th className="p-2 border">Total Qty</th>
                    <th className="p-2 border">Purchase price</th>
                    <th className="p-2 border">Payment Status</th>
                    <th className="p-2 border">Tax %</th>
                    <th className="p-2 border">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBatchDetails.map((batch, batchIndex) => {
                    const isReturn = batch.transactionType === "return";
                    const displayQty = Math.abs(batch.totalQty || 0);
                    const displayOrdered = Math.abs(batch.ordered || 0);
                    const displayFOC = Math.abs(batch.foc || 0);
                    const displayTotal = Math.abs(batch.lineTotal || 0);
                    const paymentStatus =
                      batch.paymentStatus?.toLowerCase() || "unknown";

                    return (
                      <tr
                        key={batchIndex}
                        className={`hover:bg-gray-50 border-t ${isReturn ? "bg-red-50" : ""}`}
                      >
                        <td className="p-2 border text-center">
                          {isReturn ? (
                            <Tag color="red" className="!m-0 text-xs">
                              Return
                            </Tag>
                          ) : (
                            <Tag color="green" className="!m-0 text-xs">
                              Purchase
                            </Tag>
                          )}
                        </td>

                        <td className="p-2 border text-center font-mono text-xs">
                          {batch.batch || "N/A"}
                        </td>
                        <td className="p-2 border text-center">
                          {formatDateDDMMYYYY(batch.date)}
                        </td>
                        <td className="p-2 border text-center font-medium">
                          #{batch.purchaseNumber}
                        </td>
                        <td
                          className={`p-2 border text-right ${isReturn ? "text-red-600" : ""}`}
                        >
                          {displayOrdered.toLocaleString()}
                        </td>
                        <td
                          className={`p-2 border text-right ${isReturn ? "text-red-600" : ""}`}
                        >
                          <span
                            className={batch.foc !== 0 ? "font-medium" : ""}
                          >
                            {displayFOC.toLocaleString()}
                          </span>
                        </td>
                        <td
                          className={`p-2 border text-right ${isReturn ? "text-red-600" : ""}`}
                        >
                          {displayQty.toLocaleString()}
                        </td>
                        <td className="p-2 border text-right">
                          OMR{" "}
                          {Math.abs(batch.unitPrice || 0)?.toFixed(3) ||
                            "0.000"}
                        </td>

                        <td className="p-2 border text-center">
                          {!isReturn ? (
                            <Tag
                              color={getPaymentTagColor(paymentStatus)}
                              className="!m-0 text-xs"
                            >
                              {batch.paymentStatus?.charAt(0).toUpperCase() +
                                batch.paymentStatus?.slice(1) || "N/A"}
                            </Tag>
                          ) : (
                            <span className="text-gray-400 text-xs">N/A</span>
                          )}
                        </td>
                        <td className="p-2 border text-right">
                          {batch.tax > 0 ? (
                            <span className="font-medium">{batch.tax}%</span>
                          ) : (
                            <span className="text-gray-400">0%</span>
                          )}
                        </td>
                        <td
                          className={`p-2 border text-right ${isReturn ? "text-red-600" : ""}`}
                        >
                          {isReturn ? "-OMR " : "OMR "}
                          {displayTotal.toFixed(3)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-100">
                  <tr>
                    <td
                      className="p-2 border text-right font-semibold"
                      colSpan="6"
                    >
                      {transactionType === "all" ? "NET IMPACT:" : "TOTALS:"}
                    </td>
                    <td className="p-2 border text-right font-semibold">
                      {filteredBatchDetails
                        .reduce((sum, b) => {
                          const qty = Math.abs(b.totalQty || 0);
                          return b.transactionType === "return"
                            ? sum - qty
                            : sum + qty;
                        }, 0)
                        .toLocaleString()}
                    </td>
                    <td className="p-2 border"></td>
                    <td className="p-2 border"></td>
                    <td className="p-2 border"></td>
                    <td
                      className={`p-2 border text-right font-semibold ${item.totals.spent < 0 ? "text-red-600" : ""}`}
                    >
                      {item.totals.spent < 0 ? "-OMR " : "OMR "}
                      {Math.abs(item.totals.spent)?.toFixed(3) || 0}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  useEffect(() => {
    fetchReport();
  }, []);

  return (
    <Header headers={header} setHeaders={setHeader} formdata={formdata}>
      <Loadercomponent loader={loader}>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 mt-2">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-sm text-gray-600 mb-1">
              {transactionType === "return"
                ? "Credit from Returns"
                : "Net Spent"}
            </h3>
            <p
              className={`text-2xl font-semibold ${(summary.totalSpent || 0) < 0 ? "text-red-600" : "text-gray-800"}`}
            >
              {transactionType === "return" ? "-OMR " : "OMR "}
              {Math.abs(summary.totalSpent || 0)?.toFixed(3) || 0}
            </p>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              {transactionType === "all" && (
                <>
                  <span>
                    Purchases: OMR {summary.totalPurchaseValue?.toFixed(3) || 0}
                  </span>
                  <span className="text-red-600">
                    Returns: -OMR {summary.totalReturnValue?.toFixed(3) || 0}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-sm text-gray-600 mb-1">
              {transactionType === "return"
                ? "Total Returned Qty"
                : "Net Quantity"}
            </h3>
            <p
              className={`text-2xl font-semibold ${(summary.totalEffectiveQty || 0) < 0 ? "text-red-600" : "text-gray-800"}`}
            >
              {transactionType === "return" ? "-" : ""}
              {Math.abs(summary.totalEffectiveQty || 0)?.toLocaleString() || 0}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {transactionType === "return"
                ? "Total quantity returned"
                : "Net qty across all products"}
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-sm text-gray-600 mb-1">Total Transactions</h3>
            <p className="text-2xl font-semibold text-gray-800">
              {summary.totalTransactions || 0}
            </p>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              {transactionType === "all" ? (
                <>
                  <span>Purchases: {summary.totalPurchases || 0}</span>
                  <span className="text-red-600">
                    Returns: {summary.totalReturns || 0}
                  </span>
                </>
              ) : transactionType === "return" ? (
                <span className="text-red-600">
                  Return Transactions: {summary.totalReturns || 0}
                </span>
              ) : (
                <span className="text-green-600">
                  Purchase Transactions: {summary.totalPurchases || 0}
                </span>
              )}
            </div>
          </div>

          {/* NEW PAYMENT STATUS SUMMARY CARD */}
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-sm text-gray-600 mb-2">Payment Status</h3>

            <div className="flex flex-col gap-1 text-xs text-gray-600">
              <div className="flex justify-between">
                <span className="text-green-600">Paid</span>
                <span>{summary.totalPaid || 0}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-orange-500">Partial</span>
                <span>{summary.totalPartial || 0}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-red-600">Unpaid</span>
                <span>{summary.totalUnpaid || 0}</span>
              </div>
            </div>
          </div>

        </div>

        {/* Export Buttons */}
        {/* Export Buttons */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-gray-600">
            {transactionType !== "all" && (
              <Tag
                color={transactionType === "purchase" ? "green" : "red"}
                className="ml-2"
              >
                {transactionType === "purchase"
                  ? "Purchases Only"
                  : "Returns Only"}
              </Tag>
            )}
            {transactionType === "return" && (
              <span className="ml-2 text-red-600">
                Total Credit: OMR {Math.abs(summary.totalSpent || 0).toFixed(3)}
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              className="mr-2  !bg-[#07b553] !border-[#07b553] hover:!bg-[#38cb82] hover:!border-[#38cb82]"
              type="primary"
              onClick={downloadCSV}
            >
              Download CSV
            </Button>

            <ReactToPrint
              trigger={() => (
                <Button
                  type="primary"
                  className="!bg-[#07b553] !border-[#07b553] hover:!bg-[#38cb82]"
                >
                  Print
                </Button>
              )}
              content={() => printableRef.current}
              pageStyle={`
        @page {
          size: A4 landscape;
          margin: 10mm;
        }
        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
          }
          th, td {
            border: 1px solid #000 !important;
            padding: 6px;
          }
          thead {
            display: table-header-group;
          }
          tfoot {
            display: table-row-group;
          }
          .print-only {
            display: block !important;
          }
        }
      `}
            />
          </div>
        </div>

        {/* Hidden Printable Content - This should be at the end of your component */}
        <div style={{ display: 'none' }}>
          <div ref={printableRef} className="print-only">
            {renderPrintableTable()}
          </div>
        </div>

        {/* Report Table */}
        <ScrollWrapper>
          <div className="max-h-[60vh] overflow-y-auto" ref={componentRef}>
            <table className="w-full text-sm bg-white border rounded-lg shadow-sm">
              <thead className="sticky top-0 bg-white z-10">
                <tr>
                  <th className="border p-2 text-left">Product & Supplier</th>
                  <th className="border p-2 text-left">Batches</th>
                  <th className="border p-2 text-left">Unit</th>
                  <th className="border p-2 text-left">Net Qty</th>
                  <th className="border p-2 text-left">Price/Unit</th>
                  <th className="border p-2 text-left">Net Spent</th>
                  <th className="border p-2 text-left">Transactions</th>
                  <th className="border p-2 text-left">Last Transaction</th>
                  <th className="border p-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((item, index) => {
                  const key = `${item.supplier.id}-${item.product.id}`;
                  const isExpanded = expandedRows[key];
                  const isReturn =
                    transactionType === "return" ||
                    !item.netPosition?.isNetPositive;
                  const hasReturns = item.counts?.returns > 0;
                  const hasPurchases = item.counts?.purchases > 0;

                  return (
                    <React.Fragment key={key}>
                      {/* Main Consolidated Row */}
                      <tr
                        className={`hover:bg-gray-50 ${isReturn ? "bg-red-50" : ""}`}
                      >
                        <td className="border p-2">
                          <div className="flex items-center">
                            <div className="font-medium">
                              {item.product.name}
                            </div>
                            {getNetPositionTag(item)}
                          </div>
                          <div className="text-xs text-gray-500">
                            Supplier: {item.supplier.name}
                          </div>
                          <div className="flex gap-2 mt-1">
                            {transactionType !== "return" && hasPurchases && (
                              <Tag color="green" size="small">
                                <ShoppingOutlined className="mr-1" />{" "}
                                {item.counts.purchases}
                              </Tag>
                            )}
                            {(transactionType === "all" ||
                              transactionType === "return") &&
                              hasReturns && (
                                <Tag color="red" size="small">
                                  <UndoOutlined className="mr-1" />{" "}
                                  {item.counts.returns}
                                </Tag>
                              )}
                          </div>
                        </td>

                        <td className="border p-2 text-center">
                          <Tag color={isReturn ? "red" : "blue"}>
                            {item.counts.batches || 0} batch
                            {(item.counts.batches || 0) !== 1 ? "es" : ""}
                          </Tag>
                        </td>

                        <td className="border p-2 text-center">
                          {item.product.unit || "N/A"}
                        </td>

                        {/* NET QUANTITY */}
                        <td className="border p-2 text-right">
                          <div
                            className={`font-semibold text-lg ${isReturn ? "text-red-600" : "text-gray-800"}`}
                          >
                            {isReturn && transactionType === "return"
                              ? "-"
                              : ""}
                            {Math.abs(
                              item.totals.effectiveQty || 0,
                            )?.toLocaleString() || 0}
                          </div>
                          <div className="text-xs text-gray-600">
                            <span className="mr-3">
                              {isReturn && transactionType === "return"
                                ? "Returned: "
                                : "Ordered: "}
                              {Math.abs(item.totals.ordered || 0)}
                            </span>
                            {item.totals.foc !== 0 && (
                              <span>FOC: {Math.abs(item.totals.foc || 0)}</span>
                            )}
                          </div>
                        </td>

                        {/* EFFECTIVE PRICE */}
                        <td className="border p-2 text-right">
                          <div
                            className={`font-semibold text-lg ${isReturn ? "text-red-600" : "text-gray-800"}`}
                          >
                            {isReturn && transactionType === "return"
                              ? "-OMR "
                              : "OMR "}
                            {Math.abs(
                              item.pricing.effectivePricePerUnit || 0,
                            )?.toFixed(3) || 0}
                          </div>
                          {transactionType === "all" &&
                            item.pricing.avgPurchasePrice > 0 && (
                              <div className="text-xs text-gray-600">
                                Purchase price: OMR{" "}
                                {Math.abs(
                                  item.pricing.avgPurchasePrice || 0,
                                )?.toFixed(3) || 0}
                              </div>
                            )}
                        </td>

                        {/* NET SPENT */}
                        <td className="border p-2 text-right">
                          <div
                            className={`font-semibold text-lg ${isReturn ? "text-red-600" : "text-gray-800"}`}
                          >
                            {isReturn ? "-OMR " : "OMR "}
                            {Math.abs(item.totals.spent || 0)?.toFixed(3) || 0}
                          </div>
                          {isReturn && (
                            <div className="text-xs text-red-600">
                              Credit from returns
                            </div>
                          )}
                        </td>

                        {/* TRANSACTIONS */}
                        <td className="border p-2 text-center">
                          <div className="font-semibold text-lg text-gray-800">
                            {(item.counts.purchases || 0) +
                              (item.counts.returns || 0)}
                          </div>
                          <div className="text-xs text-gray-600">
                            {item.counts.pos || 0} PO
                            {(item.counts.pos || 0) !== 1 ? "s" : ""}
                          </div>
                        </td>

                        {/* DATES */}
                        <td className="border p-2 text-center">
                          <div className="font-medium text-gray-800">
                            {formatDateDDMMYYYY(item.dates.last)}
                          </div>
                          <div className="text-xs text-gray-600">
                            First: {formatDateDDMMYYYY(item.dates.first)}
                          </div>
                        </td>

                        {/* ACTIONS */}
                        <td className="border p-2 text-center">
                          <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => toggleRowExpand(item)}
                            className="!p-0 !h-auto flex items-center text-blue-600"
                          >
                            {isExpanded ? (
                              <>
                                Hide <UpOutlined className="ml-1" />
                              </>
                            ) : (
                              <>
                                Details <DownOutlined className="ml-1" />
                              </>
                            )}
                          </Button>
                        </td>
                      </tr>

                      {/* Expanded Drill-Down Row */}
                      {renderTransactionDetails(item)}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="sticky bottom-0 bg-white z-10">
                <tr>
                  <td className="border p-2 text-center" colSpan="10">
                    <div className="text-sm text-gray-600 py-2">
                      Total Products: {reportData.length} • Showing all records
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </ScrollWrapper>

        {/* No Data State */}
        {!loader && reportData.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-2">
              No transaction data found
            </div>
            <p className="text-gray-500 text-sm">
              Try adjusting your filters or date range
            </p>
          </div>
        )}
      </Loadercomponent>
    </Header>
  );
};

export default SupplierPurchaseReport;
